import { useEffect, useState } from "react";
import {
  IconArrowRight as ArrowRight,
  IconCheck as Check,
  IconClock as Clock,
  IconMail as Mail,
  IconMapPin as MapPin,
} from "@tabler/icons-react";
import { Field } from "../shared/Field";
import { SectionHeader } from "../shared/SectionHeader";

export function ContactSection({ t }) {
  const [state, setState] = useState({ name: "", email: "", phone: "", message: "" });
  const [status, setStatus] = useState("idle");
  const [csrfToken, setCsrfToken] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    const controller = new AbortController();

    const loadToken = async () => {
      try {
        const response = await fetch("/csrf-token", {
          method: "GET",
          credentials: "same-origin",
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error(`csrf token request failed: ${response.status}`);
        }

        const data = await response.json();
        setCsrfToken(data.token || "");
      } catch (err) {
        if (err.name !== "AbortError") {
          setError("Kontaktformular konnte nicht vorbereitet werden.");
        }
      }
    };

    loadToken();
    return () => controller.abort();
  }, []);

  const onChange = (e) => setState({ ...state, [e.target.name]: e.target.value });
  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    setError("");

    try {
      const response = await fetch("/contactrequest", {
        method: "POST",
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
          ...(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
        },
        body: JSON.stringify(state),
      });

      if (!response.ok) {
        const message = await response.text();
        throw new Error(message || `request failed: ${response.status}`);
      }

      setStatus("success");
      setState({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      setStatus("error");
      setError("Senden fehlgeschlagen. Bitte versuche es später erneut.");
    }
  };

  return (
    <section id="contact" className="relative py-24 md:py-32 overflow-hidden">
      <div
        className="orb"
        style={{ width: 520, height: 520, background: "hsl(var(--brand) / 0.25)", bottom: -100, right: -120 }}
      />
      <div className="container-x relative">
        <div className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <SectionHeader eyebrow={t.contact.eyebrow} title={t.contact.title} />
            <p className="reveal mt-6 text-base md:text-lg text-muted-foreground leading-relaxed max-w-md">
              {t.contact.desc}
            </p>

            <ContactInfo items={t.contact.info} />
          </div>

          <div className="lg:col-span-7">
            <form
              onSubmit={onSubmit}
              data-testid="contact-form"
              className="reveal rounded-3xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] p-7 md:p-10"
            >
              <div className="grid gap-5 md:grid-cols-2">
                <Field
                  label={t.contact.name}
                  name="name"
                  value={state.name}
                  onChange={onChange}
                  placeholder={t.contact.namePh}
                  required
                  testid="field-name"
                />
                <Field
                  label={t.contact.email}
                  name="email"
                  type="email"
                  value={state.email}
                  onChange={onChange}
                  placeholder={t.contact.emailPh}
                  required
                  testid="field-email"
                />
                <Field
                  label={t.contact.phone}
                  name="phone"
                  type="tel"
                  value={state.phone}
                  onChange={onChange}
                  placeholder={t.contact.phonePh}
                  testid="field-phone"
                />
              </div>
              <div className="mt-5">
                <Field
                  label={t.contact.message}
                  name="message"
                  textarea
                  value={state.message}
                  onChange={onChange}
                  placeholder={t.contact.messagePh}
                  required
                  testid="field-message"
                />
              </div>

              <div className="mt-6 flex flex-wrap items-center justify-between gap-4">
                <p className="text-xs text-muted-foreground max-w-sm">{t.contact.consent}</p>
                <button
                  type="submit"
                  disabled={status === "loading" || !csrfToken}
                  className="btn-pill btn-primary px-7"
                  data-testid="submit-button"
                >
                  {status === "loading" ? t.contact.submitting : t.contact.submit}
                  <ArrowRight className="h-4 w-4" />
                </button>
              </div>

              {status === "error" && error ? (
                <div
                  data-testid="form-error"
                  className="mt-6 flex items-center gap-3 rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-700 dark:text-red-300"
                >
                  <span className="h-4 w-4 rounded-full border border-current text-[10px] leading-4 text-center">!</span>
                  {error}
                </div>
              ) : null}

              {status === "success" && (
                <div
                  data-testid="form-success"
                  className="mt-6 flex items-center gap-3 rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-700 dark:text-emerald-300"
                >
                  <Check className="h-4 w-4" />
                  {t.contact.success}
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}

function ContactInfo({ items }) {
  const icons = [Mail, MapPin, Clock];

  return (
    <div className="reveal mt-10 space-y-4">
      {items.map((it, i) => {
        const Icon = icons[i];
        return (
          <div key={i} className="flex items-center gap-4">
            <span className="grid h-11 w-11 place-items-center rounded-xl bg-brand-soft text-brand">
              <Icon className="h-5 w-5" />
            </span>
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                {it.label}
              </div>
              <div className="font-medium">{it.value}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
